// Seat silhouettes for the room design (CIN-15118). Every style is a handful of axis-aligned boxes
// in the seat's local space, exactly like the guest seat map's `regularSeatParts`: meters, origin at
// the seat's base center, seats face -z, the backrest sits at +z. `classic` is that function's output
// bit for bit (a test pins it) so a room that never picks a style renders as it always did.
// Pure module: relative imports only.
import type { Box, Vec3 } from '../seat-map-3d/scene-geometry.ts';

import type { SeatDetails, SeatExtras, SeatPose, SeatRoundness, SeatStyle } from './room-design.ts';
import { ROUNDNESS_RADIUS } from './rounded-box.ts';

export type SeatArmrests = { left: boolean; right: boolean };

/**
 * What the design adds to a seat beyond its style (CIN-15126). Without options a seat is exactly today's: one
 * material, upright, no lit cupholders, no side panels.
 */
export type SeatOptions = {
    pose?: SeatPose;
    /** The hard parts (back shell, arms, frame) are tagged `material: 'shell'`. */
    twoTone?: boolean;
    /** Recliner: a lit ring in the top of the seat's right armrest, returned in `glows`. */
    cupholders?: boolean;
    /** Recliner: a swivel tray table on a short arm over the left armrest (the hard wood of the shell). */
    trayTable?: boolean;
    /** Side panels beside a seat at the end of a row or next to an aisle; always the shell. */
    endPanel?: SeatArmrests;
};

/** A seat's parts, and the strips of light that go with it (drawn in the accent color, unlit). */
export type SeatModel = { parts: Box[]; glows: Box[] };

const vec = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
const box = (center: Vec3, size: Vec3, tiltX?: number): Box =>
    tiltX === undefined ? { center, size } : { center, size, tiltX };
/** The same part as the hard shell of a two-tone seat. */
const shell = (part: Box): Box => ({ ...part, material: 'shell' });

/** Thickness of a side panel, m. */
export const END_PANEL_THICKNESS = 0.035;
const CUPHOLDER_SIZE = 0.075;
const CUPHOLDER_BAR = 0.008;
/** How much deeper a reclined recliner is than an upright one (footrest out, back leaning): total depth factor. */
const RECLINED_REACH = 1.35;
const RECLINE_ANGLE = 0.5;

function armrestPair(
    armrests: SeatArmrests,
    armX: number,
    armY: number,
    armZ: number,
    size: Vec3,
    hard = false
): Box[] {
    const parts: Box[] = [];
    const make = hard ? (center: Vec3) => shell(box(center, size)) : (center: Vec3) => box(center, size);
    if (armrests.left) parts.push(make(vec(-armX, armY, armZ)));
    if (armrests.right) parts.push(make(vec(armX, armY, armZ)));
    return parts;
}

/** A square lit ring (four thin bars) around a cupholder, centered at (x, y, z) on an armrest top. */
function cupholderRing(x: number, y: number, z: number): Box[] {
    const half = CUPHOLDER_SIZE / 2;
    const bar = CUPHOLDER_BAR;
    return [
        box(vec(x, y + bar / 2, z - half + bar / 2), vec(CUPHOLDER_SIZE, bar, bar)),
        box(vec(x, y + bar / 2, z + half - bar / 2), vec(CUPHOLDER_SIZE, bar, bar)),
        box(vec(x - half + bar / 2, y + bar / 2, z), vec(bar, bar, CUPHOLDER_SIZE - bar * 2)),
        box(vec(x + half - bar / 2, y + bar / 2, z), vec(bar, bar, CUPHOLDER_SIZE - bar * 2))
    ];
}

/** A lit cupholder ring in the top of one armrest (`side` -1 left, 1 right), `frontZ` being where the arm's front edge is. */
function cupholderOn(side: -1 | 1, armX: number, top: number, frontZ: number): Box[] {
    return cupholderRing(side * armX, top, frontZ + CUPHOLDER_SIZE / 2 + 0.03);
}

/**
 * A swivel tray table over an armrest: a short post on the arm, a thin arm and a rounded rectangle of dark wood.
 * `side` is the armrest's side; the tray reaches over the seat, stowed at the front of the arm.
 */
function trayTable(side: -1 | 1, armX: number, armTop: number, armFrontZ: number): Box[] {
    const postZ = armFrontZ + 0.12;
    const plateX = side * (armX - 0.1);
    const plateY = armTop + 0.09;
    return [
        shell(box(vec(side * armX, armTop + 0.035, postZ), vec(0.03, 0.07, 0.03))),
        shell(box(vec(side * (armX - 0.05), plateY - 0.02, postZ), vec(0.14, 0.014, 0.03))),
        shell(box(vec(plateX, plateY, postZ - 0.03), vec(0.26, 0.02, 0.2)))
    ];
}

/** Panels beside a seat: thin hard slabs just outside its box, on the sides that have one. */
function endPanels(sides: SeatArmrests | undefined, w: number, h: number, d: number): Box[] {
    if (!sides) return [];
    const size = vec(END_PANEL_THICKNESS, h * 0.88, d * 0.94);
    return [
        ...(sides.left ? [shell(box(vec(-(w / 2 + END_PANEL_THICKNESS / 2), h * 0.44, 0), size))] : []),
        ...(sides.right ? [shell(box(vec(w / 2 + END_PANEL_THICKNESS / 2, h * 0.44, 0), size))] : [])
    ];
}

/** A slab that stands on `pivot` and leans back by `angle` about its own bottom edge: center and tilt. */
function leaned(pivot: Vec3, height: number, angle: number, width: number, thickness: number): Box {
    return box(
        vec(pivot.x, pivot.y + (Math.cos(angle) * height) / 2, pivot.z + (Math.sin(angle) * height) / 2),
        vec(width, height, thickness),
        angle
    );
}

/** A point given relative to a lean pivot, rotated about x by `angle` (positive leans toward +z) and moved back. */
function rotatedAbout(pivot: Vec3, dy: number, dz: number, angle: number): Vec3 {
    return vec(
        pivot.x,
        pivot.y + dy * Math.cos(angle) - dz * Math.sin(angle),
        pivot.z + dy * Math.sin(angle) + dz * Math.cos(angle)
    );
}

/** The two-tone classic back shell, as shares of the seat's width and depth: center z, depth, width. */
const CLASSIC_SHELL = { z: 0.305, depth: 0.07, width: 0.98 } as const;

function classicBase(w: number, h: number, d: number, twoTone = false): Box[] {
    if (!twoTone) {
        return [
            box(vec(0, h * 0.12, 0), vec(w, h * 0.24, d * 0.7)),
            box(vec(0, h * 0.5, d * 0.23), vec(w, h * 0.8, d * 0.24))
        ];
    }
    // The back is a hard shell at the rear (z 0.27d..0.34d) with the cushion in front of it (z 0.11d..0.27d). The
    // shell stays a little inside the base box's rear and side faces: two materials on one plane z-fight.
    return [
        box(vec(0, h * 0.12, 0), vec(w, h * 0.24, d * 0.7)),
        box(vec(0, h * 0.5, d * 0.19), vec(w * 0.94, h * 0.8, d * 0.16)),
        shell(box(vec(0, h * 0.5, CLASSIC_SHELL.z * d), vec(w * CLASSIC_SHELL.width, h * 0.8, d * CLASSIC_SHELL.depth)))
    ];
}

function classic(w: number, h: number, d: number, armrests: SeatArmrests, o: SeatOptions = {}): SeatModel {
    const parts = classicBase(w, h, d, o.twoTone);
    const armThick = Math.min(w, d) * 0.18;
    const armHeight = h * 0.25;
    const armY = h * 0.25 + armHeight / 2;
    const armX = w / 2 - armThick / 2;
    const armSize = vec(armThick, armHeight, d * 0.6);
    const arm = (center: Vec3) => (o.twoTone ? shell(box(center, armSize)) : box(center, armSize));
    if (armrests.left) parts.push(arm(vec(-armX, armY, -d * 0.05)));
    if (armrests.right) parts.push(arm(vec(armX, armY, -d * 0.05)));
    return { parts: [...parts, ...endPanels(o.endPanel, w, h, d)], glows: [] };
}

/** Deep, fat cushions, a tall back with a headrest, a footrest and broad arms; reclined the back leans and the footrest comes out. */
function recliner(w: number, h: number, d: number, armrests: SeatArmrests, o: SeatOptions = {}): SeatModel {
    // Broad padded arms: two neighbors' arms sit side by side and read as one wide shared armrest.
    const armThick = Math.min(w, d) * 0.26;
    const armHeight = h * 0.4;
    const armX = w / 2 - armThick / 2;
    const arms = armrestPair(
        armrests,
        armX,
        h * 0.15 + armHeight / 2,
        -d * 0.02,
        vec(armThick, armHeight, d * 0.66),
        o.twoTone
    );
    const reclined = o.pose === 'reclined';
    let parts: Box[];
    if (!reclined && !o.twoTone) {
        parts = [
            box(vec(0, h * 0.09, d * 0.05), vec(w * 0.96, h * 0.18, d * 0.62)),
            box(vec(0, h * 0.25, -d * 0.02), vec(w * 0.8, h * 0.14, d * 0.5)),
            box(vec(0, h * 0.5, d * 0.32), vec(w * 0.84, h * 0.72, d * 0.3)),
            box(vec(0, h * 0.91, d * 0.2), vec(w * 0.5, h * 0.16, d * 0.16)),
            box(vec(0, h * 0.13, -d * 0.4), vec(w * 0.7, h * 0.12, d * 0.2)),
            ...arms
        ];
    } else {
        const angle = reclined ? RECLINE_ANGLE : 0;
        const pivot = vec(0, h * 0.14, d * 0.32);
        const backHeight = h * 0.72;
        // The back: a cushion in front and, when two-tone, a shell behind it; both lean about the same pivot.
        const cushionDepth = o.twoTone ? d * 0.21 : d * 0.3;
        const backCushion = leaned(
            vec(pivot.x, pivot.y, pivot.z - (o.twoTone ? d * 0.045 : 0)),
            backHeight,
            angle,
            w * 0.84,
            cushionDepth
        );
        const backShell = o.twoTone
            ? [shell(leaned(vec(pivot.x, pivot.y, pivot.z + d * 0.105), backHeight, angle, w * 0.84, d * 0.09))]
            : [];
        // The headrest rides on the back: its center is 0.77h above and 0.12d in front of the pivot.
        const headrestAt = rotatedAbout(pivot, h * 0.77, -d * 0.12, angle);
        const headrest = box(headrestAt, vec(w * 0.5, h * 0.16, d * 0.16), angle || undefined);
        const seatTilt = reclined ? 0.12 : undefined;
        parts = [
            box(vec(0, h * 0.09, d * 0.05), vec(w * 0.96, h * 0.18, d * 0.62)),
            box(vec(0, h * 0.25, -d * 0.02), vec(w * 0.8, h * 0.14, d * 0.5), seatTilt),
            backCushion,
            ...backShell,
            headrest,
            reclined
                ? box(vec(0, h * 0.24, -d * 0.6), vec(w * 0.7, h * 0.1, d * 0.42), 0.25)
                : box(vec(0, h * 0.13, -d * 0.4), vec(w * 0.7, h * 0.12, d * 0.2)),
            ...arms
        ];
    }
    const armTop = h * 0.15 + armHeight;
    const armFrontZ = -d * 0.02 - (d * 0.66) / 2;
    // One cupholder per seat, in its right arm (or its left one when it has none on the right); the tray on the other.
    const cupSide: -1 | 1 | null = armrests.right ? 1 : armrests.left ? -1 : null;
    const traySide: -1 | 1 | null = armrests.left && cupSide === 1 ? -1 : null;
    const glows = o.cupholders && cupSide ? cupholderOn(cupSide, armX, armTop, armFrontZ) : [];
    const tray = o.trayTable && traySide ? trayTable(traySide, armX, armTop, armFrontZ) : [];
    return { parts: [...parts, ...tray, ...endPanels(o.endPanel, w, h, d)], glows };
}

/** Low and wide: a bulky base, a loose seat cushion and a back built from stacked, narrowing boxes. */
function sofaBase(w: number, h: number, d: number, twoTone = false): Box[] {
    const base = box(vec(0, h * 0.11, 0), vec(w, h * 0.22, d * 0.9));
    return [
        twoTone ? shell(base) : base,
        box(vec(0, h * 0.28, -d * 0.08), vec(w * 0.94, h * 0.14, d * 0.62)),
        box(vec(0, h * 0.42, d * 0.34), vec(w * 0.96, h * 0.42, d * 0.28)),
        box(vec(0, h * 0.66, d * 0.37), vec(w * 0.9, h * 0.2, d * 0.24)),
        box(vec(0, h * 0.82, d * 0.38), vec(w * 0.7, h * 0.16, d * 0.2))
    ];
}

function sofa(w: number, h: number, d: number, armrests: SeatArmrests, o: SeatOptions = {}): SeatModel {
    const armThick = w * 0.12;
    const armX = w / 2 - armThick / 2;
    const parts = [
        ...sofaBase(w, h, d, o.twoTone),
        ...armrestPair(armrests, armX, h * 0.3, 0, vec(armThick, h * 0.5, d * 0.85), o.twoTone)
    ];
    return { parts: [...parts, ...endPanels(o.endPanel, w, h, d)], glows: [] };
}

/** Wooden slats on end frames: a seat of two boards and a low three-slat back. */
function bench(w: number, h: number, d: number, armrests: SeatArmrests, o: SeatOptions = {}): SeatModel {
    const legThick = Math.min(w, d) * 0.09;
    const legX = w / 2 - legThick / 2;
    const slatZ = d * 0.05;
    const hard = o.twoTone ? shell : (part: Box) => part;
    const parts = [
        hard(box(vec(-legX, h * 0.2, 0), vec(legThick, h * 0.4, d * 0.7))),
        hard(box(vec(legX, h * 0.2, 0), vec(legThick, h * 0.4, d * 0.7))),
        box(vec(0, h * 0.42, -d * 0.14), vec(w, h * 0.05, d * 0.24)),
        box(vec(0, h * 0.42, d * 0.14), vec(w, h * 0.05, d * 0.24)),
        hard(box(vec(-legX, h * 0.55, d * 0.42), vec(legThick, h * 0.5, slatZ))),
        hard(box(vec(legX, h * 0.55, d * 0.42), vec(legThick, h * 0.5, slatZ))),
        hard(box(vec(0, h * 0.5, d * 0.42), vec(w - legThick * 2, h * 0.06, slatZ))),
        hard(box(vec(0, h * 0.62, d * 0.42), vec(w - legThick * 2, h * 0.06, slatZ))),
        hard(box(vec(0, h * 0.74, d * 0.42), vec(w - legThick * 2, h * 0.06, slatZ)))
    ];
    // The end frames double as arms; an armrest board sits on top only where the seat has one.
    if (armrests.left) parts.push(hard(box(vec(-legX, h * 0.55, -d * 0.05), vec(legThick, h * 0.05, d * 0.55))));
    if (armrests.right) parts.push(hard(box(vec(legX, h * 0.55, -d * 0.05), vec(legThick, h * 0.05, d * 0.55))));
    return { parts: [...parts, ...endPanels(o.endPanel, w, h, d)], glows: [] };
}

/** Height of a footstool's cushion top, m: knee height whatever the seat's own height. */
const footstoolTop = (h: number): number => Math.min(0.48, Math.max(0.3, h * 0.45));
/** A footstool's cushion: thickness as a share of its top's height, and its footprint as shares of the seat's. */
const FOOTSTOOL_CUSHION = { thickness: 0.27, width: 0.82, depth: 0.7 } as const;

/** The cushion of a footstool, the slab its number plate sits on. */
function footstoolCushion(w: number, h: number, d: number): Box {
    const top = footstoolTop(h);
    const thickness = top * FOOTSTOOL_CUSHION.thickness;
    return box(
        vec(0, top - thickness / 2, 0),
        vec(w * FOOTSTOOL_CUSHION.width, thickness, d * FOOTSTOOL_CUSHION.depth)
    );
}

/**
 * A footstool (the padded ottoman of a lounge row): a square foot plate, a short post and a thick cushion whose edges
 * the roundness pads; no back and no arms. Two-tone makes plate and post the hard shell.
 */
function footstool(w: number, h: number, d: number, o: SeatOptions = {}): SeatModel {
    const cushion = footstoolCushion(w, h, d);
    const under = cushion.center.y - cushion.size.y / 2;
    const plate = 0.016;
    const post = Math.min(cushion.size.x, cushion.size.z) * 0.18;
    const hard = o.twoTone ? shell : (part: Box) => part;
    return {
        parts: [
            hard(box(vec(0, plate / 2, 0), vec(cushion.size.x * 0.45, plate, cushion.size.z * 0.45))),
            hard(box(vec(0, (plate + under) / 2, 0), vec(post, under - plate, post))),
            cushion
        ],
        glows: []
    };
}

/**
 * One seat in the given style with everything the design adds: `armrests` says which sides keep an arm (a loveseat
 * half loses the one facing its partner), `options` the pose, materials, lit cupholders and side panels.
 * Meters, local space, base at y = 0.
 */
export function seatStyleModel(
    style: SeatStyle,
    w: number,
    h: number,
    d: number,
    armrests: SeatArmrests,
    options: SeatOptions = {}
): SeatModel {
    switch (style) {
        case 'recliner':
            return recliner(w, h, d, armrests, options);
        case 'sofa':
            return sofa(w, h, d, armrests, options);
        case 'bench':
            return bench(w, h, d, armrests, options);
        case 'footstool':
            return footstool(w, h, d, options);
        case 'classic':
        default:
            return classic(w, h, d, armrests, options);
    }
}

/**
 * Parts of one seat in the given style; `armrests` says which sides keep an arm (a loveseat half
 * loses the one facing its partner). Meters, local space, base at y = 0.
 */
export function seatStyleParts(
    style: SeatStyle,
    w: number,
    h: number,
    d: number,
    armrests: SeatArmrests,
    options?: SeatOptions
): Box[] {
    return seatStyleModel(style, w, h, d, armrests, options).parts;
}

/** The filler between the two halves of a loveseat: cushion and back only, no arms. */
export function seatStyleBridgeParts(
    style: SeatStyle,
    width: number,
    h: number,
    d: number,
    options: Pick<SeatOptions, 'pose' | 'twoTone'> = {}
): Box[] {
    const none = { left: false, right: false };
    switch (style) {
        case 'recliner':
            return recliner(width, h, d, none, options).parts;
        case 'sofa':
            return sofaBase(width, h, d, options.twoTone);
        case 'bench':
            return bench(width, h, d, none, options).parts;
        case 'footstool':
            return footstool(width, h, d, options).parts;
        case 'classic':
        default:
            return classicBase(width, h, d, options.twoTone);
    }
}

/**
 * How deep the seat's footprint is in use: a reclined recliner has its footrest out and its back leaning, and the
 * free passage in front of it (and the row behind) is measured with that. `depth` in any unit; the result in the same.
 */
export function seatReach(style: SeatStyle, pose: SeatPose, depth: number): number {
    return style === 'recliner' && pose === 'reclined' ? depth * RECLINED_REACH : depth;
}

/** The backrest slab of a seat: the surface the number plate sits on, in the seat's local space. */
export type Backrest = { center: Vec3; size: Vec3; tiltX: number };

/**
 * The slab whose rear face carries the number plate, exactly as `seatStyleParts` builds it (a test holds the two
 * together): the back cushion or, for a two-tone seat, its shell behind the cushion; the lower back of a sofa; the
 * middle slat of a bench; the cushion of a footstool. A reclined recliner's back is the slab leaned about its foot.
 */
export function backrestOf(
    style: SeatStyle,
    w: number,
    h: number,
    d: number,
    options: Pick<SeatOptions, 'pose' | 'twoTone'> = {}
): Backrest {
    switch (style) {
        case 'recliner': {
            const angle = options.pose === 'reclined' ? RECLINE_ANGLE : 0;
            const shellBack = options.twoTone === true;
            const pivot = vec(0, h * 0.14, d * 0.32 + (shellBack ? d * 0.105 : 0));
            if (!shellBack && angle === 0)
                return { center: vec(0, h * 0.5, d * 0.32), size: vec(w * 0.84, h * 0.72, d * 0.3), tiltX: 0 };
            const slab = leaned(pivot, h * 0.72, angle, w * 0.84, shellBack ? d * 0.09 : d * 0.3);
            return { center: slab.center, size: slab.size, tiltX: slab.tiltX ?? 0 };
        }
        case 'sofa':
            return { center: vec(0, h * 0.42, d * 0.34), size: vec(w * 0.96, h * 0.42, d * 0.28), tiltX: 0 };
        case 'bench': {
            const legThick = Math.min(w, d) * 0.09;
            return { center: vec(0, h * 0.62, d * 0.42), size: vec(w - legThick * 2, h * 0.06, d * 0.05), tiltX: 0 };
        }
        case 'footstool': {
            const cushion = footstoolCushion(w, h, d);
            return { center: cushion.center, size: cushion.size, tiltX: 0 };
        }
        case 'classic':
        default:
            return options.twoTone
                ? {
                      center: vec(0, h * 0.5, d * CLASSIC_SHELL.z),
                      size: vec(w * CLASSIC_SHELL.width, h * 0.8, d * CLASSIC_SHELL.depth),
                      tiltX: 0
                  }
                : { center: vec(0, h * 0.5, d * 0.23), size: vec(w, h * 0.8, d * 0.24), tiltX: 0 };
    }
}

/** Where a seat number plate goes: its center in the seat's local space, the tilt of the surface, its height. */
export type HeadrestPlate = { center: Vec3; tiltX: number; height: number };

const PLATE_OFFSET = 0.004;
const PLATE_MARGIN = 0.004;
const PLATE_MAX_HEIGHT = 0.075;
const PLATE_MIN_HEIGHT = 0.02;
const PLATE_FROM_TOP = 0.09;

/**
 * The number plate on the rear face of the backrest, looking at the row behind. The rounded seat parts have a flat
 * middle and bevels toward their edges; the bevel is `roundness x size` on each axis, so the plate is kept inside
 * the flat zone (smaller on a thin part, and lower than the top edge), lifted 4 mm off the surface along its
 * normal and turned with the backrest's tilt: it never dips into the back.
 */
export function headrestPlate(
    style: SeatStyle,
    size: Vec3,
    roundness: SeatRoundness,
    options: Pick<SeatOptions, 'pose' | 'twoTone'> = {}
): HeadrestPlate {
    const back = backrestOf(style, size.x, size.y, size.z, options);
    const flat = Math.max(0, (0.5 - ROUNDNESS_RADIUS[roundness]) * back.size.y);
    const height = Math.min(PLATE_MAX_HEIGHT, Math.max(PLATE_MIN_HEIGHT, 2 * (flat - PLATE_MARGIN)));
    const reach = Math.max(0, flat - height / 2 - PLATE_MARGIN);
    const along = Math.max(-reach, Math.min(reach, back.size.y / 2 - PLATE_FROM_TOP));
    const across = back.size.z / 2 + PLATE_OFFSET;
    const cos = Math.cos(back.tiltX);
    const sin = Math.sin(back.tiltX);
    return {
        center: vec(
            back.center.x,
            back.center.y + along * cos - across * sin,
            back.center.z + along * sin + across * cos
        ),
        tiltX: back.tiltX,
        height
    };
}

/** The design's seat settings as one value: what `seatOptionsFor` turns into the options of a single seat. */
export type SeatLook = {
    pose: SeatPose;
    twoTone: boolean;
    endPanels: boolean;
    /** Recliner extras. */
    cupholders: boolean;
    trayTable: boolean;
};

/**
 * Tolerant of a design without the seat keys (a hand-built or very old document that skipped `normalizeRoomDesign`):
 * the missing parts read as the plain seat, with the recliner cupholder on like the normalizer's default.
 */
export function seatLookOf(design: { seats?: SeatDetails; seatExtras?: SeatExtras }): SeatLook {
    return {
        pose: design.seats?.pose ?? 'upright',
        twoTone: design.seats?.twoTone ?? false,
        endPanels: design.seats?.endPanels ?? false,
        cupholders: design.seatExtras?.cupholders ?? true,
        trayTable: design.seatExtras?.trayTables ?? false
    };
}

/** The options of one seat: the look, plus the sides of this seat that face open space (used when panels are on). */
export function seatOptionsFor(look: SeatLook, ends?: SeatArmrests): SeatOptions {
    return {
        pose: look.pose,
        twoTone: look.twoTone,
        cupholders: look.cupholders,
        trayTable: look.trayTable,
        ...(look.endPanels && ends && (ends.left || ends.right) ? { endPanel: ends } : {})
    };
}

/**
 * How much lower and further back the eye of a person in a reclined recliner is than in an upright seat: `drop` in
 * meters, `backShare` as a share of the seat's depth. Null when the eye stays where it is.
 */
export type SeatEyeOffset = { drop: number; backShare: number };

export function seatEyeOffset(style: SeatStyle, pose: SeatPose): SeatEyeOffset | null {
    return style === 'recliner' && pose === 'reclined' ? { drop: 0.14, backShare: 0.24 } : null;
}

/**
 * The box around a seat's parts in its local space (a tilted part by its turned corners): what a seat is picked by and
 * what its contact shadow covers. Null without parts.
 */
export function partsBounds(parts: readonly Box[]): { center: Vec3; size: Vec3 } | null {
    if (parts.length === 0) return null;
    const min = vec(Infinity, Infinity, Infinity);
    const max = vec(-Infinity, -Infinity, -Infinity);
    for (const part of parts) {
        const cos = Math.cos(part.tiltX ?? 0);
        const sin = Math.sin(part.tiltX ?? 0);
        // Half extents of the part turned about x: y and z mix, x stays.
        const hy = (Math.abs(cos) * part.size.y + Math.abs(sin) * part.size.z) / 2;
        const hz = (Math.abs(sin) * part.size.y + Math.abs(cos) * part.size.z) / 2;
        const hx = part.size.x / 2;
        min.x = Math.min(min.x, part.center.x - hx);
        max.x = Math.max(max.x, part.center.x + hx);
        min.y = Math.min(min.y, part.center.y - hy);
        max.y = Math.max(max.y, part.center.y + hy);
        min.z = Math.min(min.z, part.center.z - hz);
        max.z = Math.max(max.z, part.center.z + hz);
    }
    return {
        center: vec((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2),
        size: vec(max.x - min.x, max.y - min.y, max.z - min.z)
    };
}
